/**
 * The phone and iPad bottom bar — `docs/proposal.md` §6 item 11 and §14.
 *
 * Below `RAIL_FROM` the summary rail has nowhere to sit, so its two lines
 * become this: the total, and the button to the signature. It carries **the
 * same two numbers the rail does**, from the same `selectionTotals`, because a
 * phone reading a different total from a laptop on the same quote is the one
 * disagreement a customer would notice and could not explain.
 *
 * **It leads the way the total card does** (Aaron's review, item 2.5): on
 * **Per month** with "Monthly $950 / month" and the total beside it, on
 * **Total** with the total and the monthly line beside it. Same figures,
 * other order.
 *
 * **`fixed`, never `sticky`.** This is the lesson `PhoneSelectionBar` already
 * paid for (`list-engine-v3.md`): sticky can only move an element inside its
 * own parent's content box, and a bar that is the last child of the page
 * column is already at that box's bottom edge with nowhere to shift to — so
 * it rides under the last card, below the fold, and the control the whole page
 * is for is the one thing you cannot reach.
 *
 * **It says how tall it is** (`onHeight`, item 2.6). The page cleared a fixed
 * 120px under the footer, and the bar that stacks — the total, the monthly
 * line, then a 52px button — is 121px before the iPhone's home-indicator inset
 * is added under it, so the footer's last line ("Powered by Rowted") sat
 * behind the bar. A number written once beside a bar whose height depends on
 * what it is saying was always going to be passed; the page now clears what
 * this measures.
 */
import { useLayoutEffect, useRef } from 'react'
import type { MonthSplit, MonthView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollars, formatDollarsExact } from '../../lib/format.js'
import { GlyphCard, GlyphChevronRight } from './glyphs.js'
import { TotalAfter, leadLabel } from './month-view.js'

/**
 * What the page leaves clear under itself before the bar has been measured —
 * and in a test, where nothing is. The tallest bar the page draws (the phone
 * bar, stacked) without a home indicator under it.
 */
export const PROPOSAL_BAR = 128

export function ProposalStickyBar({
  total, ready, monthLine, lead, onSign, signLabel = 'Review and sign', phone = true, payments,
  onHeight,
}: {
  total: number
  ready: boolean
  /** "or $288/mo + $2,663.72 once", the same function the rail prints. */
  monthLine?: string | null
  /**
   * Lead with the month: the reader is on **Per month** and it is offered.
   * The bar then says "$950 / month" first and the total second.
   */
  lead?: { cadence: MonthView; split: MonthSplit } | null
  onSign: () => void
  signLabel?: string
  /** Board 9: an iPad has room for one row — total, payments, the button. */
  phone?: boolean
  /** The rows the page is holding, for the iPad's "5 payments. $X due when you accept." */
  payments?: { count: number; deposit: number | null } | null
  /** The bar's own height, every time it changes, so the page can clear it. */
  onHeight?: (height: number) => void
}) {
  const box = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const node = box.current
    if (!node || !onHeight) return undefined
    /* jsdom lays nothing out: a 0 is "not measured", never "no bar". */
    const measure = () => { if (node.offsetHeight > 0) onHeight(node.offsetHeight) }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [onHeight])

  const month = ready && lead && lead.split.rows.length > 0 ? lead : null

  /*
   * The figure the bar leads with. A month is said the way board 12 says a
   * price — "$950" and "/ month" set apart — under the same word the rail
   * uses ("Monthly", or "From, per month" where the plans differ in length).
   */
  const figure = (size: string) => (month
    ? (
      <span className="flex items-baseline gap-1 whitespace-nowrap">
        <b className={cn('tnum font-bold tracking-[-0.02em] text-text', size)}>
          {formatDollars(month.split.perPeriod)}
        </b>
        <span className="text-[13px] font-semibold text-text-body">{`/ ${month.cadence.label}`}</span>
      </span>
      )
    : (
      <b className={cn('tnum font-bold tracking-[-0.02em] text-text', size)}>
        {ready ? formatDollarsExact(total) : '—'}
      </b>
      ))

  if (!phone) {
    return (
      <div ref={box}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface px-10
                   pb-[calc(12px+env(safe-area-inset-bottom))] pt-3.5
                   shadow-[0_-8px_24px_rgba(29,29,26,0.06)]">
        <div className="mx-auto flex max-w-[740px] items-center gap-5">
          <div className="flex shrink-0 flex-col leading-[1.15]">
            <span className="text-[12px] font-semibold text-text-faint">
              {month ? leadLabel(month.cadence, month.split) : 'Your total, with tax'}
            </span>
            {figure('text-[26px]')}
          </div>
          {ready && (month || (payments && payments.count > 0)) && (
            <span className="flex min-w-0 flex-1 items-center gap-2.5 border-l border-line pl-5 text-[13px]
                             leading-[1.4] text-text-muted">
              <GlyphCard size={16} className="shrink-0 text-brand-tx" />
              <span className="tnum">
                {/* By the month, the total is the second thing said, and first here. */}
                {month && <><TotalAfter split={month.split} />{'. '}</>}
                {payments && payments.count > 0 && (
                  `${payments.count} ${payments.count === 1 ? 'payment' : 'payments'}.`
                )}
                {payments && payments.count > 0 && payments.deposit !== null && (
                  <>
                    {' '}
                    <b className="tnum text-text-body">{formatDollarsExact(payments.deposit)}</b>
                    {' due when you accept.'}
                  </>
                )}
              </span>
            </span>
          )}
          <button
            type="button"
            onClick={onSign}
            className="ml-auto flex h-14 w-[270px] shrink-0 items-center justify-center gap-2 rounded-[14px]
                       bg-brand text-[17px] font-semibold text-brand-on"
          >
            {signLabel}
            <GlyphChevronRight size={18} />
          </button>
        </div>
      </div>
    )
  }

  /* The bar stacks whenever it has a second figure to say. */
  const second = month ? <TotalAfter split={month.split} />
    : ready && monthLine ? <>or <b className="text-[13.5px] text-text-body">{monthLine}</b></>
      : null

  return (
    <div ref={box}
      className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface px-4
                 pb-[calc(8px+env(safe-area-inset-bottom))] pt-3
                 shadow-[0_-8px_24px_rgba(29,29,26,0.06)]">
      {/*
        * **With a second figure the bar stacks** (board 13 cell 3): the lead
        * figure and the other reading on one row, the 52px button full width
        * under it. Three things on one row at 390px is a button that is no
        * longer a button, and the control the whole page exists for is the
        * last thing that may give way. Without one it stays the one row it
        * has always been.
        */}
      <div className={cn('mx-auto flex max-w-[720px] gap-3.5',
        second ? 'flex-col gap-2' : 'items-center')}>
        <div className="flex items-end justify-between gap-3">
          <div className="flex shrink-0 flex-col leading-[1.15]">
            <span className="text-[12px] font-semibold text-text-faint">
              {month ? leadLabel(month.cadence, month.split) : 'Your total'}
            </span>
            {figure('text-[22px]')}
          </div>
          {/*
            * The other reading, at the right. It may wrap onto a second line
            * on a narrow phone rather than push the lead figure out — the
            * figure is the one thing in the bar that must not give way.
            */}
          {second && (
            <span className="tnum min-w-0 text-right text-[12.5px] leading-[1.3] text-text-muted">
              {second}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onSign}
          className={cn(`flex h-[52px] items-center justify-center gap-2 rounded-[14px]
                         bg-brand text-[16px] font-semibold text-brand-on`,
            second ? 'w-full' : 'flex-1')}
        >
          {signLabel}
          <GlyphChevronRight size={18} />
        </button>
      </div>
    </div>
  )
}
